import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { timeOffByNurseIdOf } from "../contexts/WeekView/buildPtoByDayISO";
import { useOptionalWeekViewContext } from "../contexts/WeekView/WeekViewContext";
import type { ScheduleWeek, SpecificPreference } from "../utils/models";
import { scheduleKeys } from "./scheduleCache";
import { useRosterLookup } from "./useRosterLookup";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";

const NO_TIME_OFF: SpecificPreference[] = [];
const NO_WEEKS: ScheduleWeek[] = [];

/**
 * Every schedule week of the department in the cache, re-rendering when one arrives, changes or goes. The same array
 * while none of them does, so a reader can memoize on it.
 */
const useCachedScheduleWeeks = (departmentId: string): ScheduleWeek[] => {
	const queryClient = useQueryClient();
	const lastWeeksRef = useRef<ScheduleWeek[]>(NO_WEEKS);
	const subscribe = useCallback((onChange: () => void) => queryClient.getQueryCache().subscribe(onChange), [queryClient]);
	const getSnapshot = useCallback(() => {
		const weeks = queryClient.getQueriesData<ScheduleWeek>({ queryKey: scheduleKeys.department(departmentId) }).flatMap(([, week]) => (week ? [week] : []));
		const lastWeeks = lastWeeksRef.current;
		if (weeks.length === lastWeeks.length && weeks.every((week, index) => week === lastWeeks[index])) return lastWeeks;
		lastWeeksRef.current = weeks;
		return weeks;
	}, [queryClient, departmentId]);
	return useSyncExternalStore(subscribe, getSnapshot);
};

/** The weeks' time off, each row once: a several-day vacation is listed on each of its days, and in every week it touches. */
const distinctTimeOffOf = (weeks: ScheduleWeek[]): SpecificPreference[] => [
	...new Map(weeks.flatMap((week) => week.days.flatMap((day) => day.timeOff)).map((row) => [row.id, row])).values(),
];

/**
 * A nurse's time off and preferences, for the PTO warnings of shift edits. With `staffing_schedule_cache` on it's the
 * time off of every schedule week in the cache: each screen loads the weeks around the day it checks before it checks,
 * and the schedule sends each row whole. Off, on the staffing pages the shown weeks' (`timeOffByNurseId`), elsewhere
 * (Profile, Master Schedule, the staff app) the staff list's, as before.
 */
export const useNurseTimeOff = (): ((nurseId: number) => SpecificPreference[]) => {
	const weekView = useOptionalWeekViewContext();
	const { chosenDepartment } = useChosenDepartment();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const isCacheEnabled = useScheduleCacheEnabled();
	const cachedWeeks = useCachedScheduleWeeks(chosenDepartment.department_id);
	const cachedTimeOff = useMemo(() => timeOffByNurseIdOf(distinctTimeOffOf(cachedWeeks)), [cachedWeeks]);
	const timeOffByNurseId = isCacheEnabled ? cachedTimeOff : weekView?.timeOffByNurseId;

	return useCallback(
		(nurseId: number): SpecificPreference[] =>
			// DEFAULT: a nurse with no entry has no time off in the weeks, and one not on the staff list (or before it
			// loads) has none to warn about, as before.
			(timeOffByNurseId ? timeOffByNurseId.get(nurseId) : getNurseById(nurseId)?.employeeSpecificPreferences) ?? NO_TIME_OFF,
		[timeOffByNurseId, getNurseById],
	);
};
